const emptyList = {items: [], total: 0};

export const formatMoneyBR = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(amount);
};

const getSettledValue = (result) => result?.status === 'fulfilled' ? result.value : null;

const getResponsePayload = (response) => {
    const root = response?.data ?? {};
    return root?.valor ?? root?.data ?? root;
};

const getTotalFrom = (payload, fallback = 0) => Number(
    payload?.pagination?.total
    ?? payload?.pagination?.totalItems
    ?? payload?.pagination?.total_elements
    ?? payload?.meta?.total
    ?? payload?.total
    ?? payload?.totalElements
    ?? payload?.count
    ?? fallback
);

const extractListPayload = (response) => {
    const payload = getResponsePayload(response);
    if (Array.isArray(payload)) return {items: payload, total: payload.length};

    const keys = ['items', 'content', 'data', 'results', 'registros', 'conteudo'];
    for (const key of keys) {
        if (Array.isArray(payload?.[key])) {
            return {
                items: payload[key],
                total: getTotalFrom(payload, payload[key].length),
            };
        }
    }

    return emptyList;
};

const extractSettledList = (result) => {
    const response = getSettledValue(result);
    if (!response) return emptyList;
    return extractListPayload(response);
};

const getName = (item = {}) => (
    item.nome_fantasia
    || item.razao_social
    || item.nome
    || item.nome_plano
    || item.apelido
    || 'Registro'
);

const getDateValue = (item = {}) => (
    item.atualizado_em
    || item.atualizadoEm
    || item.criado_em
    || item.criadoEm
    || item.created_at
    || item.updated_at
    || null
);

export const formatRelativeTimeBR = (dateValue) => {
    if (!dateValue) return 'Data não informada';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Data não informada';

    const diffMs = Date.now() - date.getTime();
    const absMs = Math.abs(diffMs);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (absMs < minute) return 'agora';
    if (absMs < hour) {
        const value = Math.max(1, Math.round(absMs / minute));
        return `${value} ${value === 1 ? 'minuto' : 'minutos'} atrás`;
    }
    if (absMs < day) {
        const value = Math.max(1, Math.round(absMs / hour));
        return `${value} ${value === 1 ? 'hora' : 'horas'} atrás`;
    }

    const value = Math.max(1, Math.round(absMs / day));
    return `${value} ${value === 1 ? 'dia' : 'dias'} atrás`;
};

const createActivities = ({empresasItems, licencasItems}) => {
    const empresaActivities = empresasItems.map((item) => ({
        id: `empresa-${item.id || getName(item)}`,
        title: `Empresa ${getName(item)} adicionada`,
        time: formatRelativeTimeBR(getDateValue(item)),
        date: getDateValue(item),
        status: 'success',
    }));

    const licencaActivities = licencasItems.map((item) => ({
        id: `licenca-${item.id || getName(item)}`,
        title: `Licença ${item.status || 'atualizada'} para ${getName(item.empresa || item)}`,
        time: formatRelativeTimeBR(getDateValue(item)),
        date: getDateValue(item),
        status: item.status === 'ATIVA' ? 'success' : 'warning',
    }));

    return [...empresaActivities, ...licencaActivities]
        .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
        .slice(0, 5);
};

export const normalizeHomeDashboard = (responses = {}) => {
    const empresas = extractSettledList(responses.empresas);
    const sistemas = extractSettledList(responses.sistemas);
    const licencas = extractSettledList(responses.licencas);
    const financeiroPayload = getResponsePayload(getSettledValue(responses.financeiro));
    const revenue = financeiroPayload?.mrr ?? financeiroPayload?.receitaMensal ?? financeiroPayload?.receita_mensal ?? 0;

    const failedSections = Object.entries(responses)
        .filter(([, result]) => result?.status === 'rejected')
        .map(([key]) => key);

    return {
        metrics: {
            empresasAtivas: empresas.total,
            sistemasAtivos: sistemas.total,
            assinaturas: licencas.total,
            receitaMensal: Number(revenue || 0),
        },
        recentActivities: createActivities({
            empresasItems: empresas.items,
            licencasItems: licencas.items,
        }),
        failedSections,
    };
};
